/**
 * Things Bailey can take off an owner's plate.
 *
 * Written the way Muse writes its ideas: a first-person promise, then one
 * sentence on how, including where the owner decides. Each idea lists the
 * server tools it depends on (status.tools), and is hidden when any are
 * missing, so the page never offers something that will then be refused.
 *
 * `prompt` is sent as-is. `seed` goes into the message box instead, for ideas
 * that need a detail only the owner has (which offer, which goal).
 */
import { SOCIAL_VISIBLE, EMAIL_TEMPLATES_V2_VISIBLE } from '../../../config/featureFlags';

export const OWNER_IDEAS = [
    {
        // Made in the app itself (bailey/skills): shown when the tool is on
        group: 'Made for you, in your brand',
        items: [
            {
                id: 'post-free-times',
                icon: '📸',
                title: 'Your free times, as a post that’s ready to share.',
                how: 'I read your diary for the gaps and make the post in your brand, as a Story or a feed post, with the caption written. You save it, or open it in the studio to change anything.',
                short: 'Your free times as a post, in your brand. You share it.',
                needs: [],
                when: () => SOCIAL_VISIBLE,
                prompt: 'Make a post from my free times this week'
            },
            {
                id: 'week-of-posts',
                icon: '🗓️',
                title: 'A week of posts, planned for you.',
                how: 'Free slots, your best review, someone from the team and a useful tip, each drawn in your brand and planned for a good time to post. You look them over and plan them all with one tap.',
                short: 'Five posts in your brand, planned. One tap to keep them.',
                needs: [],
                when: () => SOCIAL_VISIBLE,
                prompt: 'Plan a week of Instagram posts for me'
            },
            {
                id: 'email-in-brand',
                icon: '✉️',
                title: 'An email that looks like you.',
                how: 'I write it and draw it in your own template and brand, so you see exactly what clients get. It saves as a draft, and you choose who gets it in Campaigns.',
                short: 'Written and drawn in your brand. It waits as a draft.',
                needs: [],
                when: () => EMAIL_TEMPLATES_V2_VISIBLE,
                prompt: 'Write an email to fill my quiet days, in my brand'
            },
            {
                id: 'brand-kit',
                icon: '🎨',
                title: 'Set your brand once, and everything wears it.',
                how: 'Tell me your colours, fonts and how you sound. I show it back, and one tap saves it for every email and post.',
                short: 'Your colours, fonts and tone, saved once for everything.',
                needs: [],
                when: () => EMAIL_TEMPLATES_V2_VISIBLE,
                seed: 'My brand colours are '
            }
        ]
    },
    {
        group: 'Getting clients in',
        items: [
            {
                id: 'fill-quiet-day',
                icon: '📅',
                title: 'Got a quiet day coming up? I’ll help fill it.',
                how: 'I look at your diary for the gaps, pick clients who tend to book then, and write them a text. You see who it goes to and what it costs before anything sends.',
                short: 'I find the gaps, pick who to ask and write the text. You see it all first.',
                needs: ['diary_week', 'marketable_audience', 'draft_campaign'],
                prompt: 'Help me fill my quietest day coming up'
            },
            {
                id: 'win-back',
                icon: '👋',
                title: 'Bring back clients who’ve drifted away.',
                how: 'I find who’s overdue a visit, write a win-back message in your salon’s style, and hold it until you’ve read it.',
                short: 'I find who’s overdue and write to them. It waits for your OK.',
                needs: ['marketable_audience', 'draft_campaign'],
                prompt: 'Draft a win-back text for my lapsed clients'
            },
            {
                id: 'welcome',
                icon: '💌',
                title: 'Welcome every new client for you.',
                how: 'I set up a message that goes to each new client after their first booking. It stays switched off until you’ve checked it and turned it on.',
                short: 'A message for every new client, off until you switch it on.',
                needs: ['draft_journey'],
                prompt: 'Set up a welcome message for new clients'
            }
        ]
    },
    {
        group: 'Your diary',
        items: [
            {
                id: 'booking',
                icon: '🗓️',
                title: 'Tell me the booking. I’ll get it ready.',
                how: 'Who, what and when, in your own words. I find the slot, check it fits, and open it in the diary for you to save.',
                short: 'Say it in your own words. You save it in the diary.',
                needs: ['propose_booking'],
                seed: 'Book '
            },
            {
                id: 'time-off',
                icon: '🏖️',
                title: 'Off next week? Tell me once.',
                how: 'Holidays, a dentist trip, a late start. I get it into the diary and point out any bookings it clashes with.',
                short: 'Holidays or a late start, with any clashes pointed out.',
                needs: ['propose_time_off'],
                seed: 'I need time off '
            }
        ]
    },
    {
        group: 'Money',
        items: [
            {
                id: 'how-business',
                icon: '📊',
                title: 'Ask how business is going. I’ll give you the straight answer.',
                how: 'Takings for any week or month, what changed, and who brought it in, stylist by stylist.',
                short: 'Takings, what changed, and who brought it in.',
                needs: ['revenue_summary'],
                prompt: 'How was last month?'
            },
            {
                id: 'offer-cost',
                icon: '🏷️',
                title: 'Thinking about an offer? I’ll work out what it really costs.',
                how: 'Tell me the offer, like 15% off Tuesdays. I weigh the takings you’d give up against the bookings it would need to bring in.',
                short: 'What you’d give up against what it needs to bring in.',
                needs: ['revenue_summary'],
                seed: 'What would it cost me to offer '
            }
        ]
    },
    {
        group: 'Loyalty and goals',
        items: [
            {
                id: 'loyalty',
                icon: '⭐',
                title: 'Start a loyalty card that suits your prices.',
                how: 'I suggest a card based on what clients spend and how often they come. You see it as clients will before it goes live.',
                short: 'Built from what clients spend. You see it before it’s live.',
                needs: ['propose_loyalty_scheme'],
                prompt: 'Help me start a loyalty card'
            },
            {
                id: 'goal',
                icon: '🎯',
                title: 'Set a goal and I’ll keep you on track.',
                how: 'Tell me what you want more of, like fuller Tuesdays. I measure where you’re starting, check in each week, and tell you what’s working.',
                short: 'I measure where you start and check in each week.',
                needs: ['set_goal'],
                seed: 'I want to '
            }
        ]
    },
    {
        group: 'Setup',
        items: [
            {
                id: 'online-booking',
                icon: '🧭',
                title: 'Find out what’s stopping online bookings.',
                how: 'I check your setup, booking rules and services, and tell you the few things worth fixing first.',
                short: 'The few things worth fixing first.',
                needs: ['setup_state'],
                prompt: 'What’s stopping people booking online?'
            }
        ]
    }
];

/** Employed staff see their own week, pay and clients, never the salon's. */
export const STAFF_IDEAS = [
    {
        group: 'Your work',
        items: [
            {
                id: 'my-week',
                icon: '🗓️',
                title: 'What does my week look like?',
                how: 'I run through your bookings, the gaps, and anything that’s moved.',
                short: 'Your bookings, gaps and anything that’s moved.',
                needs: ['diary_week'],
                prompt: 'What does my week look like?'
            },
            {
                id: 'my-earnings',
                icon: '💷',
                title: 'How much have I brought in?',
                how: 'Your takings for any week or month, and how they compare with before.',
                short: 'Your takings, and how they compare.',
                needs: ['staff_earnings'],
                prompt: 'How much have I brought in this month?'
            },
            {
                id: 'my-time-off',
                icon: '🏖️',
                title: 'Need time off? Tell me once.',
                how: 'I get it ready in the diary and point out any bookings it clashes with.',
                short: 'Ready in the diary, with any clashes pointed out.',
                needs: ['propose_time_off'],
                seed: 'I need time off '
            }
        ]
    }
];

const money = (n) => `£${Math.round(Number(n) || 0).toLocaleString('en-GB')}`;
const weeks = (days) => (days >= 14 ? `${Math.round(days / 7)} weeks` : `${days} days`);

/**
 * The same card, worded from this salon's own facts (/agent/glance) when
 * there is one worth saying: a live loyalty card is not "start a loyalty
 * card", a live goal reports where it stands, takings are named. No fact, or
 * nothing worth saying: the card keeps its plain wording. `live` marks a card
 * that is about their numbers. Pure, so the wording is testable.
 */
export function personalise(item, glance) {
    const g = glance || {};
    const say = (patch) => ({ ...item, ...patch, live: true });
    switch (item.id) {
        case 'win-back': {
            const o = g.overdue;
            if (!o || !(o.reachable >= 3)) return item;
            return say({
                title: `${o.reachable.toLocaleString('en-GB')} regulars are overdue. I’ll help bring them back.`,
                how: `Between them they spend about ${money(o.valueReachable)} a visit${o.typicalUsualDays ? `, most every ${weeks(o.typicalUsualDays)} or so` : ''}. I write them a friendly nudge and you check it before it goes.`,
                short: `About ${money(o.valueReachable)} a visit between them. You check the nudge first.`,
                prompt: 'Draft a friendly nudge to my overdue regulars'
            });
        }
        case 'how-business': {
            const t = g.takings;
            if (!t || t.monthToDate == null) return item;
            const trend = t.changePct == null || t.days < 7 ? ''
                : t.changePct <= -5 ? `, down ${Math.abs(t.changePct)}% on this time last month`
                    : t.changePct >= 5 ? `, up ${t.changePct}% on this time last month` : ', level with last month';
            return say({
                title: `${money(t.monthToDate)} so far this month${trend}.`,
                how: 'Ask me why, what changed, or who brought it in, stylist by stylist. Straight answers from your own takings.',
                short: 'Ask me why, or who brought it in.',
                prompt: t.changePct != null && t.changePct <= -5
                    ? 'Takings are down on last month. What’s changed, and what can we do about it?'
                    : 'How is business going this month compared with last month?'
            });
        }
        case 'offer-cost': {
            if (!g.avgBooking) return item;
            return say({
                how: `Tell me the offer, like 15% off Tuesdays. I weigh it against your average booking of ${money(g.avgBooking)} and the bookings it would need to bring in.`
            });
        }
        case 'loyalty': {
            const l = g.loyalty;
            if (!l || !l.scheme) return item;
            if (l.scheme.status === 'paused') {
                return say({
                    title: 'Your loyalty card is paused.',
                    how: `${(l.cards || 0).toLocaleString('en-GB')} clients hold a card and can’t earn while it’s off. I can switch it back on, or suggest a change first.`,
                    short: 'Clients can’t earn while it’s off.',
                    prompt: 'My loyalty card is paused. Should I switch it back on, or change it first?'
                });
            }
            const extras = [
                l.nearReward ? `${l.nearReward} ${l.nearReward === 1 ? 'is' : 'are'} one visit from a reward` : null,
                l.rewardsDue ? `${l.rewardsDue} ${l.rewardsDue === 1 ? 'has a reward' : 'have rewards'} waiting` : null
            ].filter(Boolean);
            return say({
                title: `Your loyalty card: ${(l.cards || 0).toLocaleString('en-GB')} clients on it.`,
                how: `${extras.length ? `${extras.join(', and ')}. ` : ''}I can remind the ones who are close, or tell you whether it’s bringing people back.`,
                short: extras.length ? `${extras[0]}.` : 'Is it bringing people back? Ask me.',
                prompt: 'How is my loyalty card doing, and who should I remind?'
            });
        }
        case 'goal': {
            const row = g.goals && g.goals.goals && g.goals.goals[0];
            if (!row) return item;
            const where = row.deltaPct == null ? 'first check-in coming up'
                : `${row.deltaPct >= 0 ? 'up' : 'down'} ${Math.abs(row.deltaPct)}% on where you started`;
            return say({
                title: `${row.title}: ${where}.`,
                how: `${row.summary ? `${row.summary} ` : ''}Ask me what’s working, or set another goal.`,
                short: 'Ask me what’s working.',
                prompt: `How’s my goal “${row.title}” going, and what should I try next?`,
                seed: undefined
            });
        }
        case 'online-booking': {
            const s = g.setup;
            if (!s || !s.known) return item;
            const gaps = s.gaps || [];
            if (!gaps.length) {
                return say({
                    title: 'Your setup looks good for online bookings.',
                    how: 'Nothing is holding bookings back right now. Ask me to check again any time, or look at what clients turn down.',
                    short: 'Nothing holding bookings back right now.'
                });
            }
            const first = gaps[0];
            return say({
                title: `${gaps.length === 1 ? 'One thing is' : `${gaps.length} things are`} holding bookings back.`,
                how: `${first.detail || 'Something needs setting up'}${first.blocks ? `, which stops ${first.blocks}` : ''}. I’ll take you through the fix.`,
                short: first.detail || 'I’ll take you through the fix.'
            });
        }
        default:
            return item;
    }
}

/** The groups this account can use, with empty groups dropped, worded from their own facts where there are some. */
export function ideasFor(status, glance) {
    const tools = new Set((status && status.tools) || []);
    const staff = status && status.principalType === 'employed-staff';
    return (staff ? STAFF_IDEAS : OWNER_IDEAS)
        .map((g) => ({ ...g, items: g.items.filter((i) => i.needs.every((t) => tools.has(t)) && (!i.when || i.when())).map((i) => personalise(i, glance)) }))
        .filter((g) => g.items.length);
}

/**
 * "How I work", built from what the server says this account allows, so it
 * never claims more than it can do.
 */
const SEE = {
    'salon:read': 'Your salon details and services',
    'clients:read': 'Your clients and their visits',
    'diary:read': 'Your diary',
    'finance:read': 'Your takings',
    'finance:staff-detail': 'Takings by stylist',
    'marketing:read': 'Your messages and automations',
    'loyalty:read': 'Your loyalty card',
    'brand:read': 'Your logo, colours and brand style'
};
const DO = [
    ['draft_campaign', 'Write texts and emails for you to check'],
    ['draft_journey', 'Draft automations, switched off until you turn them on'],
    ['propose_booking', 'Get bookings ready for you to save in the diary'],
    ['propose_time_off', 'Get time off ready for you to save'],
    ['propose_loyalty_scheme', 'Design a loyalty card for you to check'],
    ['set_goal', 'Set goals with you and check how they’re going'],
    ['revenue_summary', 'Answer questions about your business']
];
const ASKS = [
    ['send_campaign', 'Sending any text or email. You see who it goes to and what it costs, then approve it.'],
    ['propose_booking', 'Saving a booking. I open it in the diary and you save it.'],
    ['draft_journey', 'Switching on an automation. That’s always your tap.']
];

export function howIWork(status) {
    const tools = new Set((status && status.tools) || []);
    const scopes = (status && status.scopes) || [];
    return {
        see: scopes.map((s) => SEE[s]).filter(Boolean),
        can: DO.filter(([t]) => tools.has(t)).map(([, text]) => text),
        asks: ASKS.filter(([t]) => tools.has(t)).map(([, text]) => text),
        canAct: tools.has('send_campaign') || tools.has('draft_campaign')
    };
}
